import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  deleteRequest,
  getRequest as get,
  postRequest as post,
} from 'api/utils/HttpMethods';
import { claimQueryKeys } from './claim';
import queryString from 'api/utils/queryStringBuilder';
import type { Comment } from '@billing/validation/comment';

export const claimCommentQueryKeys = {
  byClaim: (claimID: string) => ['claimComments', claimID] as const,
};

export const getClaimComments = async (claimID: string): Promise<Comment[]> => {
  const response = await get<Comment[]>(
    'QBilling',
    `/resource/comment/claim${queryString({ claimID })}`
  );
  return response ?? [];
};

export const useClaimComments = (
  claimID?: string,
  enabled = true
): UseQueryResult<Comment[], Error> =>
  useQuery({
    queryKey: claimCommentQueryKeys.byClaim(claimID ?? ''),
    queryFn: () => getClaimComments(claimID!),
    enabled: Boolean(claimID) && enabled,
    retry: false,
  });

export const createClaimComment = async (
  claimID: string,
  text: string
): Promise<void> => {
  const response = await post<
    { id: string },
    { claimID: string; text: string }
  >('QBilling', '/resource/comment/claim', { claimID, text });
  if (!response?.id) throw new Error('Claim comment creation failed');
};

export const useCreateClaimComment = (
  claimID: string
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => createClaimComment(claimID, text),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: claimCommentQueryKeys.byClaim(claimID),
        }),
        queryClient.invalidateQueries({ queryKey: claimQueryKeys.all }),
      ]);
    },
  });
};

export const deleteClaimComment = async (
  claimID: string,
  commentID: string
): Promise<void> => {
  const response = await deleteRequest<{ success: boolean }>(
    'QBilling',
    '/resource/comment/claim',
    {
      claimID,
      commentID,
    }
  );
  if (!response?.success) throw new Error('Claim comment deletion failed');
};

export const useDeleteClaimComment = (
  claimID?: string
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentID: string) => deleteClaimComment(claimID!, commentID),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: claimCommentQueryKeys.byClaim(claimID ?? ''),
        }),
        queryClient.invalidateQueries({ queryKey: claimQueryKeys.all }),
      ]);
    },
  });
};
